/**
 * Cell Component Registry
 *
 * Central registry mapping entity types and field names to their canonical
 * cell rendering components. Entity tables are the source of truth - this
 * registry imports and organizes their cell components for reuse.
 *
 * Usage:
 * ```typescript
 * const CellComponent = getCellComponent('Asset', 'status');
 * if (CellComponent) {
 *   return <CellComponent entity={asset} />;
 * }
 * ```
 */

import { ComponentType } from 'react';

// Import Asset cells
import { AssetStatusComponent } from '@/sections/asset/components/badges';
import {
  AssetNameCell,
  AttackSurfaceCell,
  OriginCell as AssetOriginCell,
  PortCell,
  ProtocolCell,
  TagsCell as AssetTagsCell,
} from '@/sections/asset/components/cells';
// Import Seed cells
import { SeedStatusComponent } from '@/sections/seeds/components/badges';
import { OriginCell as SeedOriginCell } from '@/sections/seeds/components/OriginCell';
// Import Technology cells
import {
  ProductCell,
  TagsCell as TechnologyTagsCell,
  VendorCell,
  VersionCell,
} from '@/sections/technology/components/cells';

// Import shared cells
import { DateCell } from './DateCell';
import { EmailCell } from './EmailCell';

// Type definitions
export type EntityType =
  | 'Asset'
  | 'Seed'
  | 'Technology'
  | 'Vulnerability'
  | 'ActiveDirectory';

/**
 * Cell components can have different prop signatures:
 * - Some use generic 'entity' prop
 * - Some use entity-specific props like 'asset', 'seed', etc.
 *
 * This type accommodates both patterns.
 */
export type CellComponent = ComponentType<any>;

/**
 * Registry structure mapping entity types to their field-specific cell components
 */
interface CellRegistry {
  shared: Record<string, CellComponent>;
  Asset: Record<string, CellComponent>;
  Seed: Record<string, CellComponent>;
  Technology: Record<string, CellComponent>;
  Vulnerability: Record<string, CellComponent>;
  ActiveDirectory: Record<string, CellComponent>;
}

/**
 * Cell Component Registry
 *
 * Organization:
 * - shared: Components used across multiple entity types
 * - [EntityType]: Entity-specific cell components
 */
export const CELL_REGISTRY: CellRegistry = {
  /**
   * Shared cells used by multiple entity types
   */
  shared: {
    email: EmailCell,
    created: DateCell,
    visited: DateCell,
    updated: DateCell,
  },

  /**
   * Asset-specific cell components
   */
  Asset: {
    status: AssetStatusComponent,
    identifier: AssetNameCell,
    attackSurface: AttackSurfaceCell,
    origins: AssetOriginCell,
    port: PortCell,
    protocol: ProtocolCell,
    tags: AssetTagsCell,
  },

  /**
   * Seed-specific cell components
   */
  Seed: {
    status: SeedStatusComponent,
    origin: SeedOriginCell,
    // TODO Phase 2: Add SeedTagsCell if different from Asset
  },

  /**
   * Technology-specific cell components
   */
  Technology: {
    tags: TechnologyTagsCell,
    vendor: VendorCell,
    product: ProductCell,
    version: VersionCell,
    name: ProductCell, // 'name' is essentially the product
  },

  /**
   * Vulnerability-specific cell components
   */
  Vulnerability: {
    // TODO Phase 2: Add SeverityCell, StatusCell, etc.
  },

  /**
   * Active Directory-specific cell components
   */
  ActiveDirectory: {
    // TODO: Add AD-specific cells if any
  },
};

/**
 * Get cell component for a specific entity type and field
 *
 * Lookup strategy:
 * 1. Check entity-specific registry
 * 2. Fall back to shared registry
 * 3. Return null if not found (caller should use fallback)
 *
 * @param entityType - Entity type (Asset, Seed, Technology, etc.)
 * @param field - Field name (status, tags, email, etc.)
 * @returns Cell component or null
 *
 * @example
 * ```typescript
 * const StatusCell = getCellComponent('Asset', 'status');
 * // Returns: AssetStatusComponent
 *
 * const EmailCell = getCellComponent('Asset', 'email');
 * // Returns: EmailCell (from shared)
 *
 * const UnknownCell = getCellComponent('Asset', 'unknownField');
 * // Returns: null
 * ```
 */
export function getCellComponent(
  entityType: EntityType,
  field: string
): CellComponent | null {
  // Check entity-specific registry first
  const entityRegistry = CELL_REGISTRY[entityType];
  if (entityRegistry && entityRegistry[field]) {
    return entityRegistry[field];
  }

  // Fall back to shared registry
  if (CELL_REGISTRY.shared[field]) {
    return CELL_REGISTRY.shared[field];
  }

  // Not found - caller should use fallback
  return null;
}

/**
 * Check if a cell component exists for entity type and field
 *
 * @param entityType - Entity type
 * @param field - Field name
 * @returns True if component exists in registry
 */
export function hasCellComponent(
  entityType: EntityType,
  field: string
): boolean {
  return getCellComponent(entityType, field) !== null;
}

/**
 * Get all registered fields for an entity type
 *
 * Useful for debugging and documentation
 *
 * @param entityType - Entity type
 * @returns Array of field names with registered components
 */
export function getRegisteredFields(entityType: EntityType): string[] {
  const entityFields = Object.keys(CELL_REGISTRY[entityType] || {});
  const sharedFields = Object.keys(CELL_REGISTRY.shared);
  return [...new Set([...entityFields, ...sharedFields])].sort();
}
